import { joinClassNames } from '@vertex-protocol/web-common';
import { UpDownChevronIcon, Z_INDEX } from '@vertex-protocol/web-ui';
import {
  FundingTokenSymbol,
  getFundingSourceChainsForToken,
} from 'client/modules/collateral/nadoDeposit/config/fundingRoutes';
import { useMemo, useRef, useState, useEffect } from 'react';

interface Props {
  selectedChainId: number;
  onChainSelected: (chainId: number) => void;
  /** Filter displayed chains to those supporting this funding token. Defaults to 'USDT'. */
  tokenSymbol?: FundingTokenSymbol;
}

/**
 * Chain selector — renders an inline expandable list inside the dialog.
 * Avoids portal/z-index issues that occur with Radix DropdownMenu inside dialogs.
 *
 * Filters the displayed chains to Across funding source chains for the given token.
 */
export function NadoChainSelect({ selectedChainId, onChainSelected, tokenSymbol = 'USDT' }: Props) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayChains = useMemo(
    () => getFundingSourceChainsForToken(tokenSymbol),
    [tokenSymbol],
  );

  const selectedChain = useMemo(
    () => displayChains.find((c) => c.chainId === selectedChainId),
    [displayChains, selectedChainId],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={joinClassNames(
          'bg-surface-1 hover:bg-surface-2 border-stroke',
          'flex w-full items-center justify-between rounded-sm border px-3 py-2.5',
          'transition-colors outline-none',
        )}
      >
        <div className="flex items-center gap-x-2.5">
          <ChainIcon chainId={selectedChain?.chainId} />
          <span className="text-text-primary text-sm">
            {selectedChain?.name ?? 'Select chain'}
          </span>
        </div>
        <UpDownChevronIcon open={open} className="text-text-tertiary size-3" />
      </button>

      {open && (
        <div
          className={joinClassNames(
            'bg-surface-2 border-stroke shadow-elevation-strong',
            `absolute left-0 right-0 top-full mt-1 rounded-sm border ${Z_INDEX.popover}`,
            'flex flex-col py-1',
          )}
        >
          {displayChains.map((chain) => (
            <button
              key={chain.chainId}
              type="button"
              onClick={() => {
                onChainSelected(chain.chainId);
                setOpen(false);
              }}
              className={joinClassNames(
                'flex items-center gap-x-2.5 px-3 py-2 text-left',
                'transition-colors hover:bg-overlay-hover',
                chain.chainId === selectedChainId
                  ? 'text-text-primary'
                  : 'text-text-secondary',
              )}
            >
              <ChainIcon chainId={chain.chainId} />
              <span className="text-sm">{chain.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Chain icon — official network SVGs inlined as React components.
 */
export function ChainIcon({ chainId }: { chainId: number | undefined }) {
  const className = 'size-5 flex-shrink-0';

  if (chainId === 57073) {
    // Ink — official logo (symbol only, purple gradient)
    return (
      <svg viewBox="0 0 30.7028 30.7028" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="inkGradientChainIcon" x1="0" y1="15.3514" x2="30.7028" y2="15.3514" gradientUnits="userSpaceOnUse">
            <stop stopColor="#B99FFF" />
            <stop offset="1" stopColor="#674DBC" />
          </linearGradient>
        </defs>
        <path fillRule="evenodd" clipRule="evenodd" d="M15.3514 0C23.8297 -3.70601e-07 30.7028 6.87306 30.7028 15.3514C30.7028 23.8297 23.8297 30.7028 15.3514 30.7028C6.87306 30.7028 3.70608e-07 23.8297 0 15.3514C-3.70608e-07 6.87306 6.87306 3.70601e-07 15.3514 0ZM15.4744 28.7529C16.6877 28.7286 17.5425 27.8821 17.5425 26.8406C17.5425 25.7839 16.7098 25.0009 15.6438 25.0009C15.1074 25.0009 14.864 25.0009 14.621 24.9908C14.4221 24.9825 14.2235 24.9674 13.8646 24.9401L13.8492 24.939L13.6216 24.9216C12.5739 24.8424 11.6888 24.0687 11.6888 23.0088C11.6888 21.9521 12.5524 21.0961 13.6216 21.0961H15.613C16.679 21.0961 17.5425 20.24 17.5425 19.1832C17.5425 18.1234 16.679 17.2674 15.613 17.2674H9.06903C7.99992 17.2674 7.1364 16.4113 7.1364 15.3545C7.1364 14.2947 7.99992 13.4354 9.06903 13.4354H22.048C23.114 13.4354 23.9775 12.5794 23.9775 11.5227C23.9775 10.466 23.114 9.60993 22.048 9.60993H13.6216C12.5524 9.60993 11.6888 8.75389 11.6888 7.69088C11.6888 6.63414 12.6713 5.85424 13.6216 5.77809C14.5717 5.70196 14.6203 5.70196 15.613 5.70196C16.6055 5.70196 17.5425 4.92204 17.5425 3.86218C17.5425 2.8208 16.8401 1.97431 15.471 1.94993C15.4312 1.94958 15.3913 1.94941 15.3514 1.94941C7.94968 1.94941 1.94938 7.9497 1.94938 15.3514C1.94938 22.7458 7.93773 28.7415 15.3293 28.7535H15.3735C15.4072 28.7534 15.4408 28.7532 15.4744 28.7529Z" fill="url(#inkGradientChainIcon)" />
      </svg>
    );
  }
  if (chainId === 1) {
    // Ethereum
    return (
      <svg viewBox="0 0 20 20" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="10" fill="#627EEA" />
        <path d="M10 3v5.2l4.4 2L10 3Z" fill="#fff" fillOpacity={0.6} />
        <path d="M10 3L5.6 10.2l4.4-2V3Z" fill="#fff" />
        <path d="M10 13.5v3.5l4.4-6.1-4.4 2.6Z" fill="#fff" fillOpacity={0.6} />
        <path d="M10 17v-3.5l-4.4-2.6L10 17Z" fill="#fff" />
        <path d="M10 12.7l4.4-2.5L10 8.2v4.5Z" fill="#fff" fillOpacity={0.2} />
        <path d="M5.6 10.2l4.4 2.5V8.2l-4.4 2Z" fill="#fff" fillOpacity={0.6} />
      </svg>
    );
  }
  if (chainId === 42161) {
    // Arbitrum
    return (
      <svg viewBox="0 0 17 17" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.455" y="0.666" width="16" height="16" rx="8" fill="#213147" />
        <path d="M9.345 9.578l-.581 1.594a.14.14 0 000 .139l.998 2.74 1.157-.667-1.387-3.787c-.034-.086-.154-.086-.187-.019z" fill="#12AAFF" />
        <path d="M10.51 6.9c-.034-.087-.154-.087-.187 0l-.581 1.593a.14.14 0 000 .14l1.637 4.487 1.157-.667L10.51 6.9z" fill="#12AAFF" />
        <path d="M8.455 3.41c.029 0 .058.01.082.024l4.406 2.544c.053.029.082.086.082.144v5.088c0 .058-.034.115-.082.144l-4.406 2.544a.16.16 0 01-.164 0L3.967 11.354a.163.163 0 01-.082-.144V6.117c0-.057.034-.115.082-.144l4.406-2.544a.16.16 0 01.082-.02zm0-.744a.6.6 0 00-.456.12L3.592 5.33a.6.6 0 00-.456.787v5.088a.6.6 0 00.456.787l4.407 2.544a.6.6 0 00.912 0l4.406-2.544a.6.6 0 00.456-.787V6.117a.6.6 0 00-.456-.787L8.91 2.786a.6.6 0 00-.456-.12z" fill="#9DCCED" />
        <path d="M5.536 13.125l.408-1.113.816.677-.763.7-.461-.264z" fill="#213147" />
        <path d="M8.08 5.757H6.962a.15.15 0 00-.187.13L4.38 12.453l1.156.667 2.64-7.233a.08.08 0 00-.091-.135l-.005.005z" fill="white" />
        <path d="M10.038 5.757H8.92a.15.15 0 00-.187.13l-2.736 7.497 1.157.667 2.976-8.165a.08.08 0 00-.091-.134v.005z" fill="white" />
      </svg>
    );
  }
  if (chainId === 8453) {
    return (
      <svg viewBox="0 0 20 20" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="10" fill="#0052FF" />
        <path d="M10.08 15.86c3.2 0 5.8-2.6 5.8-5.8s-2.6-5.8-5.8-5.8c-3.04 0-5.54 2.34-5.78 5.32h7.66v.96H4.3c.24 2.98 2.74 5.32 5.78 5.32Z" fill="white" />
      </svg>
    );
  }
  return <div className="bg-surface-3 size-5 rounded-full flex-shrink-0" />;
}
